// utils/apiQueryBuilder.js

import { cleanFilterData, buildQueryParams } from "./FilterUtils";

/**
 * Builds a complete API URL with query parameters for pagination, filtering, and sorting
 * @param {string} endpoint - The base API endpoint
 * @param {Object} options - Configuration options
 * @param {number} options.page - Current page number (1-based)
 * @param {number} options.pageSize - Items per page
 * @param {Object} options.filters - Filter object
 * @param {Array} options.sortModel - Sorting model from DataGrid
 * @param {Object} options.fieldMappings - Field mappings for filters
 * @param {Object} options.rangeFields - Range field configurations
 * @param {Object} options.valueTransformers - Value transformers for specific fields
 * @returns {string} Complete API URL with query parameters
 */
export const buildApiQuery = (
  endpoint,
  {
    page = 1,
    pageSize = 20,
    filters = {},
    sortModel = [],
    fieldMappings = {},
    rangeFields = {},
    valueTransformers = {},
  }
) => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page);
  queryParams.append("pagination", pageSize);

  // Apply filters if any
  const cleanedFilters = cleanFilterData(filters);
  const filterParams = buildQueryParams(
    cleanedFilters,
    fieldMappings,
    rangeFields,
    valueTransformers
  );

  // Add sorting parameters if available
  let sortParams = "";
  if (sortModel && sortModel.length > 0) {
    const { field, sort } = sortModel[0];
    let apiField = field;

    // Check if the field is in fieldMappings and use the API field name
    if (fieldMappings[field]) {
      apiField = fieldMappings[field][0];
    }

    // For fields in rangeFields, use the apiField property
    for (const [key, value] of Object.entries(rangeFields)) {
      if (field === key) {
        apiField = value.apiField;
        break;
      }
    }

    sortParams = `sort[0]=${apiField}:${sort}`;
  }

  // Combine all params
  let apiUrl = `${endpoint}?${queryParams.toString()}`;

  if (filterParams) {
    apiUrl += `&${filterParams}`;
  }

  if (sortParams) {
    apiUrl += `&${sortParams}`;
  }

  return apiUrl;
};
